import React, { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue
} from 'react-native-reanimated'
import { CommonActions, useNavigation } from '@react-navigation/native'
import {
  SDKQueryClient,
  SDKQueryClientPersister,
  useIsAuthed,
  useSDK,
  useUser,
  useUserProfile
} from '@vatom/sdk/react'
import { translate } from '@vatom/utils'
import { theme } from '@vatom/wombo'
import * as AuthSession from 'expo-auth-session'
import { observer } from 'mobx-react-lite'
import { getSnapshot } from 'mobx-state-tree'
import { Box, Center, Text } from 'native-base'

import { HeaderButtonBack, ScreenHeader, ScreenHeaderButton } from '../../components/ScreenHeader'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { Title } from '../../components/Title'
import { useBusinessTheme } from '../../hooks/useBusinessTheme'
import { useIsDesktop } from '../../hooks/useIsDesktop'
import logger from '../../logger'
import { useStore } from '../../models'
import { AppRoutes } from '../../navigators'

import { ProfileMetamask } from './components/ProfileMetamask'
import { ProfileUserBio } from './components/ProfileUserBio'
import { ProfileUserCard } from './components/ProfileUserCard'
import { StatsCard } from './components/StatsCard'
import { IdentityList } from './partials/IdentityList'
import MetamaskProfile from './partials/MetamaskProfile'
import VatomProfile from './partials/VatomProfile'
import VersionInfo from './partials/VersionInfo'
import { ButtonTransparent, QrCode } from './partials'

const isWeb = Platform.OS === 'web'

export const ProfileUser = observer(() => {
  const [walletAddress, setWalletAddress] = useState('')
  const store = useStore()
  const [qrCodeVisible, setQrCodeVisible] = useState<boolean>(false)
  const isDesktop = useIsDesktop()

  const scrollOffsetY = useSharedValue(0)
  const scrollHandler = useAnimatedScrollHandler(event => {
    scrollOffsetY.value = event.contentOffset.y
  })
  const Max_Header_Height = 100
  const Min_Header_Height = 70
  const scrollDiff = Max_Header_Height - Min_Header_Height // 30

  const animatedHeaderStyles = useAnimatedStyle(() => {
    const height = interpolate(scrollOffsetY.value, [0, scrollDiff], [100, 60], {
      extrapolateRight: Extrapolation.CLAMP,
      extrapolateLeft: Extrapolation.CLAMP
    })
    return {
      height: height
    }
  })
  const animatedTitleStyles = useAnimatedStyle(() => {
    const show = interpolate(scrollOffsetY.value, [130, 200], [0, 1], {
      extrapolateRight: Extrapolation.CLAMP,
      extrapolateLeft: Extrapolation.CLAMP
    })
    return {
      display: show === 0 ? 'none' : 'flex'
    }
  })

  const animatedScrollStyles = useAnimatedStyle(() => {
    const value = interpolate(scrollOffsetY.value, [0, 30], [-50, 0], {
      extrapolateRight: Extrapolation.CLAMP,
      extrapolateLeft: Extrapolation.CLAMP
    })

    return {
      marginTop: value
    }
  })

  const navigation = useNavigation()
  const isLoggedIn = useIsAuthed()
  const sdk = useSDK()
  const user = useUser()
  const { data: userProfile } = useUserProfile(user?.sub ?? '')
  const { isDark } = useBusinessTheme()

  const {
    clientId: oClientId,
    scopes,
    useProxy,
    discoveryUrl,
    redirectUri
  } = getSnapshot(sdk.config).authentication

  const clientId = isWeb ? '94JHkdj8jF83jfFF2LI8Q4' : oClientId
  const postLogoutRedirectUri = isWeb
    ? `${window.location.origin}/logout-callback`
    : 'com.vatom://logout-callback'

  // discovery service
  // https://id.vatom.com/.well-known/openid-configuration
  const discovery = AuthSession.useAutoDiscovery(discoveryUrl)
  const session = sdk.dataPool.sessionStore.vatomIncSessionToken
  logger.info('[ProfileUser] vatomIncSessionToken', session)
  const [request, , promptAsync] = AuthSession.useAuthRequest(
    {
      clientId,
      redirectUri,
      // id_token will return a JWT token
      responseType: AuthSession.ResponseType.Code,
      prompt: AuthSession.Prompt.Consent,
      // prompt: AuthSession.Prompt.Login,
      // responseType: AuthSession.ResponseType.Token,
      scopes,
      extraParams: {
        post_logout_redirect_uri: postLogoutRedirectUri,
        id_token_hint: session?.idToken ?? ''
      }
    },
    {
      // ...discovery,
      authorizationEndpoint: encodeURI(`https://id.vatom.com/session/end`)
    }
  )

  useEffect(() => {
    if (!isLoggedIn) {
      const resetAction = CommonActions.reset({
        index: 0,
        routes: [{ name: AppRoutes.connect }]
      })
      navigation.dispatch(resetAction)
    }
  }, [isLoggedIn, navigation])

  const logoutOrDisconnect = async () => {
    logger.info('[ProfileUser.logoutOrDisconnect] isLoggedIn', isLoggedIn)
    try {
      if (isLoggedIn && discovery) {
        if (Platform.OS === 'android') {
          await promptAsync({
            // @ts-expect-error not really sure why we need this but I'm afraid it's important that would cause an issue upon removal
            useProxy,
            windowFeatures: {
              createTask: false
            }
          })
        }

        if (isWeb && request?.url) {
          window.location.replace(request.url)
        }
      }
    } catch (e) {
      logger.error('[ProfileUser.logoutOrDisconnect] error', e)
    } finally {
      if (!isWeb) {
        // Log out of the user store
        if (Platform.OS !== 'android') {
          AuthSession.dismiss()
        }
        store.linking.clearLinkingUrl()
        sdk.service.setToken(undefined)
        sdk.dataPool.sessionStore.clear()
        sdk.dataPool.user.clearCache()
        SDKQueryClient.clear()
        SDKQueryClientPersister.removeClient()
        sdk.dataPool.user.logout()
      }
    }
  }
  const backgroundColor = sdk.dataPool.sessionStore.isLoggedInWithVatom ? 'white' : 'gray.50'

  const openWalletQrCode = (walletAddress: string) => {
    setWalletAddress(walletAddress)
    setQrCodeVisible(true)
  }

  const openUserQrCode = () => {
    setWalletAddress(user?.sub ?? '')
    setQrCodeVisible(true)
  }

  const onPressEdit = () => {
    if (isWeb) {
      window.open(AppRoutes.editProfile, 'profile')
      return
    }
    navigation.navigate(AppRoutes.editProfile)
  }

  const onPressManage = () => {
    if (isWeb) {
      window.open(AppRoutes.manageAccounts, 'profile')
      return
    }
    navigation.navigate(AppRoutes.manageAccounts)
  }

  const brandColor = isDark ? theme.colors.systemColorsDark.orange : theme.colors.brand[500]
  const brandColorLight = isDark ? theme.colors.systemColorsDark.orange : '#FF7A00'

  const backdropProps = !isDesktop
    ? {
        zIndex: 0,
        backgroundColor: {
          linearGradient: { colors: [brandColor, brandColorLight] }
        }
      }
    : undefined

  const onPressBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.navigate(AppRoutes.home)
    }
  }

  return (
    <ScreenWrapper statusBarBackgroundColor={brandColor} statusBarStyle={'light-content'}>
      <Box
        _light={{
          backgroundColor: theme.colors.white
        }}
        _dark={{
          backgroundColor: theme.colors.grayCool[900]
        }}
      >
        <Animated.View
          style={[{ zIndex: 0, position: 'relative', overflow: 'hidden' }, animatedHeaderStyles]}
        >
          <ScreenHeader
            headerTextColor={theme.colors.white}
            headerProps={{
              zIndex: 0,
              backgroundColor: 'transparent'
            }}
            backdropProps={backdropProps}
            headerLeft={() =>
              !isDesktop && (
                <ScreenHeaderButton onPress={onPressBack}>
                  <HeaderButtonBack color={theme.colors.white} />
                </ScreenHeaderButton>
              )
            }
            headerRight={() => <ScreenHeaderButton />}
          >
            <Animated.View
              style={[
                {
                  alignSelf: 'stretch',
                  alignItems: 'center',
                  justifyContent: 'center'
                },
                animatedTitleStyles
              ]}
            >
              {user?.name && (
                <Title
                  preset="h5"
                  variant="bold"
                  color={theme.colors.white}
                  _web={{
                    color: isDesktop ? theme.colors.textLightMode[900] : theme.colors.white
                  }}
                >
                  {user?.name}
                </Title>
              )}
            </Animated.View>
          </ScreenHeader>
        </Animated.View>
        <Animated.ScrollView
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          onScroll={scrollHandler}
          style={animatedScrollStyles}
        >
          {isLoggedIn ? (
            <ProfileUserCard
              avatarUrl={user?.picture}
              name={user?.name}
              username={userProfile?.preferred_username}
              onPressQrCode={openUserQrCode}
            />
          ) : (
            <ProfileMetamask />
          )}
          <Center>
            {user?.bio && <ProfileUserBio text={user?.bio} marginTop={4} />}

            {user?.website && (
              <Center marginY={2}>
                <Text color={'#2EA7FF'}>{user?.website}</Text>
              </Center>
            )}
            <StatsCard
              userId={userProfile?.id ?? ''}
              containerProps={{
                marginTop: 8
              }}
            />
          </Center>

          <ButtonTransparent
            title={translate('profileScreen.editAccount')}
            onPress={onPressEdit}
            height={10}
            marginTop={6}
            marginBottom={4}
          />
          <Box
            width={'100%'}
            height={2}
            _dark={{
              backgroundColor: theme.colors.grayCool[900]
            }}
            backgroundColor={theme.colors.grayCool[100]}
          />

          <IdentityList openQrCode={openWalletQrCode} />
          <ButtonTransparent
            title={translate('profileScreen.manageAccount')}
            onPress={onPressManage}
            height={10}
            marginTop={6}
            marginBottom={4}
          />

          <Box height={6} />

          {isLoggedIn ? (
            <VatomProfile
              background={backgroundColor}
              user={user}
              logoutOrDisconnect={logoutOrDisconnect}
              openWalletQrCode={openWalletQrCode}
            />
          ) : (
            <MetamaskProfile
              background={backgroundColor}
              openWalletQrCode={openWalletQrCode}
              logoutOrDisconnect={logoutOrDisconnect}
              user={user}
            />
          )}

          {walletAddress !== '' && (
            <QrCode
              qrCodeVisible={qrCodeVisible}
              setQrCodeVisible={setQrCodeVisible}
              qrLink={`https://wallet.vatom.com/receive/${user?.sub}`}
            />
          )}

          <ButtonTransparent
            marginTop={12}
            title={translate('common.logout')}
            onPress={logoutOrDisconnect}
          />

          <Box safeAreaBottom paddingY={8}>
            <VersionInfo />
          </Box>
        </Animated.ScrollView>
      </Box>
    </ScreenWrapper>
  )
})

export default ProfileUser
